/**
 * Global Settings Composable - Provides access to site-wide global settings
 * Uses the GO Commerce GraphQL Cache system for optimized caching
 */
import type { GlobalSettingsType } from "@/types";
import { executeGraphQLQuery } from "../utils/graphqlCache";

export function useGlobalSettings() {
  const mainStore = useMainStore();

  // Initialize with data from store if available
  const initialSettings = mainStore.getGlobalSettings || null;
  const settings = useState<GlobalSettingsType | null>(
    "globalSettings",
    () => initialSettings
  );
  const isLoading = ref(false);

  // GraphQL query for global settings
  const GLOBAL_SETTINGS_QUERY = `
    query GetGlobalSettings {
      globalSetting {
        documentId
        siteName
        CardType
        createAccountInfo
        createAccountContent
        createAccountConnfirmationContent
        useVoucherForRecharge
        textFaqPage
        imgSimCard {
          name
          url
          alternativeText
          width
          height
        }
        imgCart {
          name
          url
          alternativeText
          width
          height
        }
        imgMascot {
          name
          url
          alternativeText
          width
          height
        }
        imgLinkedFlyBus {
          name
          url
          alternativeText
          width
          height
        }
        imgLinkedOnePass {
          name
          url
          alternativeText
          width
          height
        }
        qrCodeImage {
          name
          url
          alternativeText
          width
          height
        }
        header {
          __typename
          ... on ComponentNavigationNavigationItem {
            id
            title
            subitems {
              id
              type
              url
              linkText
              target
              page {
                documentId
                title
                slug
              }
            }
            link {
              id
              type
              url
              linkText
              target
              page {
                documentId
                title
                slug
              }
            }
          }
        }
        footer {
          __typename
          ... on ComponentNavigationFooterColumn {
            id
            links {
              id
              url
              linkText
              type
              target
              page {
                documentId
                title
                slug
              }
            }
          }
        }
        navLogo {
          name
          url
          alternativeText
          width
          height
        }
        loginPageFields {
          loginPageTitle
          loginPageSubtitle
          catchConnectMessage
          termsAndConditions
          onepassIcon {
            name
            url
            alternativeText
            width
            height
          }
        }
        confirmDetailsFields {
          confirmDetailsHeader
          confirmDetailsFooter
        }
        contentBlocks {
          code
          content
        }
        publishedAt
        createdAt
        updatedAt
      }
    }
  `;

  /**
   * Fetch global settings using the GO Commerce GraphQL Cache system
   * The GraphQL cache handles caching with proper TTLs and shared request prevention
   */
  const fetchSettings = async (forceFresh = false) => {
    console.log("[LOGIN-INFO] fetchSettings called, forceFresh:", forceFresh);

    // Check store first if we have fresh data
    const storeSettings = mainStore.getGlobalSettings;
    if (storeSettings && !forceFresh) {
      console.log("[LOGIN-SUCCESS] Using cached settings from store");
      settings.value = storeSettings;
      return storeSettings;
    }

    // Prevent multiple concurrent API calls
    if (isLoading.value) {
      console.log("[LOGIN-WAIT] Already loading, waiting...");
      // Wait for the current fetch to complete
      await new Promise<void>((resolve) => {
        const checkLoading = () => {
          if (!isLoading.value) {
            resolve();
          } else {
            setTimeout(checkLoading, 50);
          }
        };
        checkLoading();
      });
      return settings.value;
    }

    try {
      isLoading.value = true;
      console.log("[LOGIN-QUERY] Starting GraphQL query for global settings");
      console.log("[LOGIN-QUERY] Query:", GLOBAL_SETTINGS_QUERY);

      // Define the response type
      type ResponseType = {
        globalSetting: GlobalSettingsType;
      };

      // Execute the GraphQL query using the GO Commerce GraphQL Cache system
      // It automatically handles caching with TTLs and prevents duplicate requests
      const result = await executeGraphQLQuery<ResponseType>(
        {
          query: GLOBAL_SETTINGS_QUERY,
          operationName: "GetGlobalSettings",
        },
        0, // DISABLE CACHE for testing
        forceFresh
      );

      console.log("[LOGIN-QUERY] GraphQL result received:", result);

      if (result?.globalSetting) {
        // Store in local state and main store
        settings.value = result.globalSetting;
        mainStore.updateGlobalSettings(result.globalSetting);
        console.log("[LOGIN-SUCCESS] Global settings loaded successfully");
        console.log(
          "[LOGIN-SUCCESS] LoginPageFields:",
          result.globalSetting.loginPageFields
        );
        console.log(
          "[CONFIRM DETAILS-SUCCESS] ConfirmDetailsFields:",
          result.globalSetting.confirmDetailsFields
        );
        return result.globalSetting;
      } else {
        console.log("[LOGIN-WARN] No globalSetting in result");
      }

      return null;
    } catch (error) {
      console.error("[LOGIN-ERROR] Error fetching global settings:", error);
      console.error(
        "[LOGIN-ERROR] Error details:",
        JSON.stringify(error, null, 2)
      );
      return null;
    } finally {
      isLoading.value = false;
      console.log("[LOGIN-INFO] fetchSettings completed");
    }
  };

  return {
    settings,
    isLoading: readonly(isLoading),
    fetchSettings,
  };
}
